<template>
  <footer>
    <slot name="primary-section" />

    <!-- university-services-menu section -->
    <section class="bg-secondary text-dark-3">
      <div class="container">
        <div class="row">
          <div class="col">
            <div
              class="d-flex flex-column flex-lg-row align-items-center justify-content-start justify-content-lg-end py-space-xxs py-lg-0">
              <img
                data-src="https://live-asuocms.ws.asu.edu/sites/default/files/2024-09/innovation_ranking.png"
                alt="Repeatedly ranked #1 on 20+ lists in the last 3 years"
                class="fs-lazy order-2 innovation-image" />
              <slot name="secondary-menu" />
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- university-legal-compliance-menu section -->
    <section :class="legalClass" class="bg-light-2">
      <div class="container">
        <div class="row">
          <div class="col">
            <nav
              class="nav grid-flex-menu order-2 order-lg-1"
              aria-label="University Legal and Compliance">
              <a
                v-for="(item, index) in tertiaryItems"
                :key="index"
                :href="item.uri"
                :target="formatLinkTarget(item.target)"
                @click="handleTertiaryMenuLinkClick(item)"
                class="nav-link text-dark-3 menu-item py-space-xxs py-lg-0"
                rel="noopener noreferrer">
                {{ item.text }}
              </a>
            </nav>
          </div>
        </div>
      </div>
    </section>
  </footer>
</template>

<script setup lang="ts">
import lozad from "lozad";
import { computed, onMounted } from "vue";

type TrackingData = {
  event: string;
  name: string;
  action: string;
  type: string;
  region: string;
  section: string;
  text: string;
  component?: string;
};

export interface TertiaryItem {
  text: string;
  uri: string;
  target: string;
}

interface Props {
  displayApplyNow?: boolean;
  displayRfiCta?: boolean;
  tertiaryItems?: TertiaryItem[];
}

const props = withDefaults(defineProps<Props>(), {
  displayApplyNow: false,
  displayRfiCta: false,
  tertiaryItems: () => {
    return [
      {
        text: "Copyright and Trademark",
        uri: "https://www.asu.edu/copyright/",
        target: "BLNK",
      },
      {
        text: "Accessibility",
        uri: "https://www.asu.edu/accessibility/",
        target: "BLNK",
      },
      {
        text: "Privacy",
        uri: "https://www.asu.edu/privacy/",
        target: "BLNK",
      },
      {
        text: "Terms of Use",
        uri: "https://www.asu.edu/tou/",
        target: "BLNK",
      },
      {
        text: "Emergency",
        uri: "https://www.asu.edu/emergency/",
        target: "BLNK",
      },
    ];
  },
});

// events
const emits = defineEmits<{
  /**
   *  Emitted when a tertiary menu item is clicked.
   */
  (e: "tertiaryMenuLinkClick", event: TrackingData): void;
}>();

const legalClass = computed(() => {
  return props.displayApplyNow || props.displayRfiCta
    ? `pb-space-xl pt-space-xs py-lg-space-xs`
    : `py-space-xs`;
});

const formatLinkTarget = (target: string) => {
  if (target == "SELF") {
    return "_self";
  } else {
    return "_blank";
  }
};
const handleTertiaryMenuLinkClick = (item: TertiaryItem) => {
  const trackingData: TrackingData = {
    event: "link",
    name: "onclick",
    action: "click",
    type: "internal link",
    region: "footer",
    section: "tertiary footer",
    text: item.text.toLowerCase(),
    component: "footer - innovation",
  };

  emits("tertiaryMenuLinkClick", trackingData);
};

onMounted(() => {
  if (typeof window !== "undefined" && window !== null) {
    const observer = lozad(".fs-lazy");
    observer.observe();
  }
});
</script>

<style lang="scss" scoped>
nav.grid-flex-menu {
  display: grid;
  grid-template-rows: repeat(3, auto);
  grid-auto-flow: column;
  grid-auto-columns: minmax(150px, 1fr);
  width: 100%;
}

img.innovation-image {
  width: 100%;
  height: auto;
}

/* Large devices (desktops, 992px and up) */
@media (min-width: 992px) {
  img.innovation-image {
    height: 100px;
    width: auto;
  }
  nav.grid-flex-menu {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-start;
  }
  a.menu-item:hover {
    text-decoration: underline;
  }
}
</style>
